// useTypeFieldsFieldReorder.js

import { useCallback, useState } from 'react';

// Utilities
import {
  buildOverrideFieldsFromOrder,
  makeOrderedFieldPayload,
  moveOrderedFieldByName,
} from 'utilities/NodeFieldReorder';

// Services
import { syncFormsWithTypeSchema } from 'services/forms/FormSyncService';
import { reorderNodeTypeFields } from 'services/nodetypes/NodeTypeMutationService';

// Constants
import { FIELD_LOG_MESSAGES, FIELD_MESSAGES } from 'node/form/constants/fieldMessages';

/**
 * useTypeFieldsFieldReorder
 *
 * Manages drag state and reorder persistence for type-level field rows.
 *
 * @param {array} fields - Rendered field list in visual order.
 * @param {array} resolvedFields - Fully resolved field list used for rollback.
 * @param {array|null} overrideFields - Current local override fields.
 * @param {function} setOverrideFields - Setter for override fields state.
 * @param {string} resolvedTypeKey - Effective node type key for reorder mutation.
 * @param {string|null} resolvedBaseKey - Effective base key for reorder mutation.
 * @param {function} refreshNodeTypesCache - Cache refresh helper after schema mutations.
 * @param {boolean} isSystemType - Whether editing/reorder is disabled.
 *
 * @returns {object} Drag state and drag/drop handlers.
 */
export default function useTypeFieldsFieldReorder({
  fields,
  resolvedFields,
  overrideFields,
  setOverrideFields,
  resolvedTypeKey,
  resolvedBaseKey,
  refreshNodeTypesCache,
  isSystemType = false,
}) {
  const [draggingFieldName, setDraggingFieldName] = useState('');
  const [dragOverFieldName, setDragOverFieldName] = useState('');

  const handleDragStart = useCallback((field, event) => {
    if (!field?.name || isSystemType) return;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', field.name);
    setDraggingFieldName(field.name);
  }, [isSystemType]);

  const handleDragOver = useCallback((field, event) => {
    if (!draggingFieldName || !field?.name || isSystemType) return;
    if (field.name === draggingFieldName) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverFieldName(field.name);
  }, [draggingFieldName, isSystemType]);

  const handleDragEnd = useCallback(() => {
    setDraggingFieldName('');
    setDragOverFieldName('');
  }, []);

  const handleDrop = useCallback(async (field, event) => {
    if (!draggingFieldName || !field?.name || isSystemType) return;
    event.preventDefault();

    if (field.name === draggingFieldName) {
      handleDragEnd();
      return;
    }

    const displayOrder = makeOrderedFieldPayload(fields);
    const nextOrder = moveOrderedFieldByName(displayOrder, draggingFieldName, field.name);

    if (!nextOrder) {
      handleDragEnd();
      return;
    }

    const previousOverride = overrideFields;
    const nextOverride = buildOverrideFieldsFromOrder({ ordered: nextOrder, resolvedFields, fields });
    setOverrideFields(nextOverride);
    handleDragEnd();

    try {
      await reorderNodeTypeFields({
        typeKey: resolvedTypeKey,
        baseKey: resolvedBaseKey,
        orderedFields: nextOrder,
      });
      await refreshNodeTypesCache({
        baseKey: resolvedBaseKey,
        typeKey: resolvedTypeKey,
        fields: nextOverride,
      });
      await syncFormsWithTypeSchema({
        typeDef: { schema: { fields: nextOverride } },
        typeKey: resolvedTypeKey,
        baseKey: resolvedBaseKey,
      }).catch((error) => {
        console.error('[useTypeFieldsFieldReorder] form sync failed after reorder', error);
      });
    } catch (error) {
      console.error(FIELD_LOG_MESSAGES.REORDER_FIELDS_FAILED, error);
      setOverrideFields(previousOverride || null);
      await refreshNodeTypesCache({
        baseKey: resolvedBaseKey,
        typeKey: resolvedTypeKey,
        fields: Array.isArray(previousOverride) ? previousOverride : resolvedFields,
      });
      alert(error?.message || FIELD_MESSAGES.REORDER_FAILED);
    }
  }, [
    draggingFieldName,
    fields,
    handleDragEnd,
    isSystemType,
    overrideFields,
    refreshNodeTypesCache,
    resolvedFields,
    resolvedTypeKey,
    resolvedBaseKey,
    setOverrideFields,
  ]);

  return {
    draggingFieldName,
    dragOverFieldName,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDrop,
  };
}
